
export type StepKey = 'did' | 'expected' | 'guess';

export interface Step {
  key: StepKey;
  label: string;
  short: string;
  editable: boolean;
}

export const STEPS: Step[] = [
  { key: 'did', label: 'Что делали', short: 'Делал', editable: false },
  { key: 'expected', label: 'Что ждали', short: 'Ждал', editable: true },
  { key: 'guess', label: 'Предполагаемые причина и решение', short: 'Предполагаемые причина и решение', editable: true },
];

export const EDITABLE_STEPS = STEPS.filter((step) => step.editable);
export const REQUIRED_STEPS = EDITABLE_STEPS;

export type Answers = Record<StepKey, string>;

export const emptyAnswers = (): Answers => ({ did: '', expected: '', guess: '' });

export function composeDescription(answers: Answers): string {
  return STEPS.map((step) => [step.label, (answers[step.key] ?? '').trim()] as const)
    .filter(([, text]) => text)
    .map(([label, text]) => `${label}: ${text}`)
    .join('\n');
}

/** Words of an answer: the text without "[снимок N]" marks — a field holding only marks is not answered. */
export const hasWords = (text: string | undefined): boolean => !!(text ?? '').replace(/\[снимок \d+\]/g, '').trim();

export const answeredCount = (answers: Answers): number => REQUIRED_STEPS.filter((step) => hasWords(answers[step.key])).length;

interface ActionLike {
  type: string;
  target?: string;
  /** Recorded inside the host menu (`ownSelectors`). */
  own?: boolean;
  delta?: number;
  via?: string;
}

/** The report's own clicks; the host menu that opened it is marked `own` when recorded (`ownSelectors`). */
const OWN = /bug-report|в окне «Баг-репорт»/;
/** A scenario click, not the report itself or the host menu (also for actions taken while the panel was minimized). */
export const isScenarioAction = (action: string | ActionLike | undefined): boolean => {
  if (typeof action === 'object' && action?.own) return false;
  const target = typeof action === 'string' ? action : action?.target;
  return !target || !OWN.test(target);
};
const CHECKBOX = /^input\.[^\s]*\[type=checkbox\]/;
const CHECKED = /\[checked\]/;
const TAB = /\[role=tab\]/;
const SELECT_ALL = /^(Выбрать|Разобрать новые)/;
const RELEASE = /^Отпустить выбор/;

function labelOf(target: string): string | null {
  const named = target.match(/«([^«»]{1,40})»/);
  if (named) return named[1].trim();
  const text = target.match(/"([^"]{1,30})"/);
  return text && !/^div\b|^span\b|^li\b/.test(target) ? text[1].trim() : null;
}

function messages(count: number): string {
  const tail = count % 10;
  const tens = count % 100;
  if (tail === 1 && tens !== 11) return `${count} сообщение`;
  if (tail >= 2 && tail <= 4 && (tens < 10 || tens >= 20)) return `${count} сообщения`;
  return `${count} сообщений`;
}

export function stepsFromActions(actions: readonly ActionLike[] | null | undefined, limit = 3): string {
  const phrases: string[] = [];
  let picked: number | null | undefined;
  let lastCheckbox = 0;
  const flush = () => {
    if (picked === null) phrases.push('сообщения выделены');
    else if (picked !== undefined && picked > 0) phrases.push(picked === 1 ? 'сообщение выделено' : `${messages(picked)} выделены`);
    picked = undefined;
  };
  const push = (phrase: string) => {
    if (phrases[phrases.length - 1] !== phrase) phrases.push(phrase);
  };
  for (const action of actions ?? []) {
    if (action.type === 'select' && typeof action.delta === 'number') {
      const undo = action.via === 'shift' ? lastCheckbox : 0;
      lastCheckbox = 0;
      if (picked !== null) picked = Math.max(0, (picked ?? 0) - undo + action.delta);
      continue;
    }
    if (action.type !== 'click' || !action.target || action.own || OWN.test(action.target)) continue;
    const target = action.target;
    if (CHECKBOX.test(target) && /«Выделить»/.test(target)) {
      lastCheckbox = CHECKED.test(target) ? 1 : -1;
      if (picked !== null) picked = Math.max(0, (picked ?? 0) + lastCheckbox);
      continue;
    }
    lastCheckbox = 0;
    const label = labelOf(target);
    if (!label) continue;
    const count = label.match(/:\s*(\d+)\s*$/);
    const name = label.replace(/:\s*\d+\s*$/, '');
    if (SELECT_ALL.test(name)) {
      flush();
      picked = count ? Number(count[1]) : null;
      continue;
    }
    if (RELEASE.test(name)) {
      picked = undefined;
      push(`кнопка «${name}»`);
      continue;
    }
    flush();
    push(TAB.test(target) ? `вкладка «${name}»` : `кнопка «${name}»`);
  }
  flush();
  const text = phrases.slice(-limit).join(' → ');
  return text ? text[0].toUpperCase() + text.slice(1) : '';
}

const norm = (text: string): string => text.toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ').trim();

export function mergeStep(current: string, next: string, applied = ''): string {
  const add = next.trim();
  const have = current.trim();
  if (!add) return current;
  if (!have) return add;
  if (norm(have).includes(norm(add))) return current;
  if (applied.trim() && current.includes(applied.trim())) return current.replace(applied.trim(), add);
  if (norm(add).includes(norm(have))) return add;
  return `${current.trimEnd()}\n${add}`;
}

export function mergeAnswers(current: Answers, next: Partial<Answers>, applied: Partial<Answers> = {}, untouched: Partial<Answers> = {}): Answers {
  const out = { ...current };
  for (const step of EDITABLE_STEPS) {
    const base = untouched[step.key] !== undefined && current[step.key] === untouched[step.key] ? '' : current[step.key];
    out[step.key] = mergeStep(base, next[step.key] ?? '', applied[step.key]);
  }
  return out;
}

export function describeWithWishes(answers: Answers, wishes: readonly string[]): string {
  const list = wishes.map((wish) => wish.trim()).filter(Boolean);
  return [composeDescription(answers), list.length ? `Пожелания: ${list.join('; ')}` : ''].filter(Boolean).join('\n');
}

export interface AgentNote {
  understanding: Partial<Answers>;
  agreed: boolean;
}

export function agentLines(answers: Answers, note: AgentNote | null | undefined): string {
  if (!note) return '';
  const lines = STEPS.flatMap((step) => {
    const said = note.understanding[step.key]?.trim() ?? '';
    if (!said || norm(answers[step.key] ?? '').includes(norm(said))) return [];
    return [`— ${step.label}: ${said}`];
  });
  if (lines.length === 0) return '';
  return [`Со слов агента (${note.agreed ? 'подтверждено' : 'не подтверждено'}):`, ...lines].join('\n');
}

export function describeScenarios(
  list: readonly Answers[],
  wishes: readonly string[],
  notes: readonly (AgentNote | null | undefined)[] = [],
): string {
  const items = wishes.map((wish) => wish.trim()).filter(Boolean);
  const bodyOf = (answers: Answers, index: number) => [composeDescription(answers), agentLines(answers, notes[index])].filter(Boolean).join('\n');
  if (list.length === 1) return [bodyOf(list[0], 0), items.length ? `Пожелания: ${items.join('; ')}` : ''].filter(Boolean).join('\n');
  const blocks = list.map((answers, index) => {
    const body = bodyOf(answers, index);
    return body ? `Сценарий ${index + 1}\n${body}` : '';
  });
  return [...blocks, items.length ? `Пожелания: ${items.join('; ')}` : ''].filter(Boolean).join('\n\n');
}

export function scenarioFileName(scenario: number, kind: 'shot' | 'trace', index: number, ext: string): string {
  if (kind === 'trace') return `s${scenario}-trace${index > 1 ? index : ''}.${ext}`;
  return `s${scenario}-shot${index}.${ext}`;
}

export function prefillSince(actions: readonly (ActionLike & { at?: string })[] | null | undefined, at: string, limit = 3): string {
  return stepsFromActions(
    (actions ?? []).filter((action) => (action.at ?? '') >= at),
    limit,
  );
}

export function plural(count: number, forms: readonly [string, string, string]): string {
  const tail = count % 10;
  const tens = count % 100;
  const form = tail === 1 && tens !== 11 ? forms[0] : tail >= 2 && tail <= 4 && (tens < 10 || tens >= 20) ? forms[1] : forms[2];
  return `${count} ${form}`;
}

/** A shot's place in the text: N is the thumbnail number within its scenario (the same K as in `sN-shotK`). */
export const shotMark = (n: number): string => `[снимок ${n}]`;

const MARK_RE = /\[снимок (\d+)\]/g;

/** The numbers of the marks standing in the texts (Мт3: they are the numbers of the scenario's shots, 1..N). */
export function markNumbers(...texts: string[]): Set<number> {
  const numbers = new Set<number>();
  for (const text of texts) for (const match of text.matchAll(MARK_RE)) numbers.add(Number(match[1]));
  return numbers;
}

/**
 * Where marks may go near `at`: never inside a word or another mark — the caret moves forward past the word (the run of
 * non-space characters) and past a mark until it stands on a boundary.
 */
export function markBoundary(text: string, at: number): number {
  let pos = Math.max(0, Math.min(text.length, at));
  for (;;) {
    const start = pos;
    for (const match of text.matchAll(MARK_RE)) {
      const end = match.index! + match[0].length;
      if (match.index! < pos && pos < end) pos = end;
    }
    while (pos > 0 && pos < text.length && /\S/.test(text[pos - 1]) && /\S/.test(text[pos])) pos += 1;
    if (pos === start) return pos;
  }
}

/**
 * Puts marks at `at` (moved to a word / mark boundary) with a space on each side where the text has none; returns the
 * text and the caret after them.
 */
export function insertShotMarks(text: string, at: number, numbers: readonly number[]): { text: string; caret: number } {
  const pos = markBoundary(text, at);
  const before = text.slice(0, pos);
  const after = text.slice(pos);
  const marks = numbers.map(shotMark).join(' ');
  const head = before && !/\s$/.test(before) ? `${before} ` : before;
  const tail = after && !/^\s/.test(after) ? ` ${after}` : after;
  return { text: `${head}${marks}${tail}`, caret: head.length + marks.length };
}

/**
 * The one region a textarea edit replaced: `prev.slice(start, end)` became `insert`. The shortest such region; when the
 * edit could have been elsewhere (the same text repeats: a mark among marks), `caret` — where the field left the caret,
 * right after what went in — picks the place.
 */
function editRegion(prev: string, next: string, caret?: number): { start: number; end: number; insert: string } {
  let start = 0;
  while (start < prev.length && start < next.length && prev[start] === next[start]) start += 1;
  let suffix = 0;
  while (suffix < prev.length - start && suffix < next.length - start && prev[prev.length - 1 - suffix] === next[next.length - 1 - suffix]) suffix += 1;
  const end = prev.length - suffix;
  const insert = next.slice(start, next.length - suffix);
  if (caret !== undefined) {
    const at = caret - insert.length;
    const shifted = { start: at, end: end + (at - start), insert: next.slice(at, caret) };
    if (at !== start && at >= 0 && shifted.end <= prev.length && prev.slice(0, at) + shifted.insert + prev.slice(shifted.end) === next) return shifted;
  }
  return { start, end, insert };
}

/**
 * Report 95: a mark is one piece in the field. `next` is what the textarea holds after one edit of `prev` (`caret` —
 * where it left the caret); where the edit cut into a mark, the text is corrected: a deletion (Backspace, Delete, a cut)
 * takes every touched mark whole with one of its spaces, as `renumberShotMarks` drops one; typed or pasted text lands
 * after the mark it was put into; a selection that ends inside a mark lets it be. A mark selected whole goes as the
 * person meant. Null — `next` is fine as it is.
 */
export function keepMarksWhole(prev: string, next: string, caret?: number): { text: string; caret: number } | null {
  if (prev === next) return null;
  const marks = [...prev.matchAll(MARK_RE)].map((match) => ({ start: match.index!, end: match.index! + match[0].length }));
  if (marks.length === 0) return null;
  const { start, end, insert } = editRegion(prev, next, caret);
  const inside = (pos: number, mark: { start: number; end: number }) => mark.start < pos && pos < mark.end;
  let from = start;
  let to = end;
  if (insert === '') {
    let touched = false;
    for (const mark of marks) {
      if (mark.start < end && start < mark.end && (inside(start, mark) || inside(end, mark))) {
        touched = true;
        from = Math.min(from, mark.start);
        to = Math.max(to, mark.end);
      }
    }
    if (!touched) return null;
    if (prev[from - 1] === ' ') from -= 1;
    if (prev[to] === ' ') to += 1;
    const glue = from > 0 && to < prev.length && /\S/.test(prev[from - 1]) && /\S/.test(prev[to]) ? ' ' : '';
    return { text: prev.slice(0, from) + glue + prev.slice(to), caret: from + glue.length };
  }
  for (const mark of marks) {
    if (inside(from, mark)) from = mark.end;
    if (inside(to, mark)) to = mark.start;
  }
  if (to < from) to = from;
  if (from === start && to === end) return null;
  return { text: prev.slice(0, from) + insert + prev.slice(to), caret: from + insert.length };
}

/** Renumbers marks; `null` drops the mark with one of its spaces, leaving one space where it stood between two words. */
export function renumberShotMarks(text: string, map: (n: number) => number | null): string {
  return text.replace(/( ?)\[снимок (\d+)\]( ?)/g, (whole: string, lead: string, n: string, trail: string, offset: number) => {
    const next = map(Number(n));
    if (next !== null) return `${lead}${shotMark(next)}${trail}`;
    const prev = text[offset - 1];
    const following = text[offset + whole.length];
    return prev !== undefined && following !== undefined && /\S/.test(prev) && /\S/.test(following) ? ' ' : '';
  });
}

/**
 * A picked rephrase variant goes into the editable fields (Рф5). The model numbers marks as the sent text does (ready
 * shots only, `readyAt[k-1]` is the thumbnail number of the k-th); they are mapped back, marks of unknown shots drop, and a
 * shot left without a mark gets one at the end of «Ждал» (Мт3). A field the variant leaves empty keeps its text.
 */
export function applyRephrase(
  answers: Answers,
  variant: { expected: string; guess: string },
  readyAt: readonly number[],
  count: number,
  marks: boolean,
): Answers {
  const back = (text: string) =>
    marks ? renumberShotMarks(text.trim(), (k) => (readyAt[k - 1] !== undefined && readyAt[k - 1] <= count ? readyAt[k - 1] : null)) : text.trim();
  let expected = variant.expected.trim() ? back(variant.expected) : answers.expected;
  const guess = variant.guess.trim() ? back(variant.guess) : answers.guess;
  if (marks) {
    const have = markNumbers(expected, guess);
    const missing = Array.from({ length: count }, (_, i) => i + 1).filter((n) => !have.has(n));
    if (missing.length > 0) expected = insertShotMarks(expected, expected.length, missing).text;
  }
  return { ...answers, expected, guess };
}
